import Interface from "./Interface";
import { Block, Firmware } from "../Types";
import { hasCobberSettings } from "../Lib/Devices";

class USBDevice extends Interface {

    // Based on Chipset
    hardware_device_name = {
        0x41: 'EFM8UB10F16G_QFN28',
        0x43: 'EFM8UB10F16G_QFN20',
        0x45: 'EFM8UB11F16G_QSOP24',
        0x49: 'EFM8UB10F8G_QFN20',
        0x65: 'Cobber'
    };

    hardware_device_top_address: any = {
        0x41: 0x37ff,
        0x43: 0x37ff,
        0x45: 0x37ff,
        0x49: 0x17ff,
        0x65: 0x6dff
    };

    firmwareSettings: any = {
        0x65: {
            after: 512,
            start: 1,
            preserve: [
                {
                    start: 512,
                    end: 687
                }
            ]
        }
    };
    // End Chipset

    // Based on Product ID
    product_name: any = {
        0x4A: "EBIKE_MTB900",
        0x4B: "EBIKE_MTB1300",
        0x4C: "EBIKE_MTB1800",
        0x4D: "EBIKE_MTB2300",
        0x52: "CobberMR",
        0x53: "CobberMF",
        0x5A: "CobberMRS",
        0x5F: "CobberRR",
        0x60: "CobberRF",
    };
    // End Product ID

    firmware_type_name: any = {
        0: "Unknown Type",
        1: "Bootloader Code",
        2: "Authentication Utility",
        3: "User Firmware"
    };

    HARDWARE_DEVICE_TYPE = null;
    DEVICE_ID: number | null = null;
    BOOTLD_ID: number | null = null;
    DEVICE_UUID = null;
    BOOTLOADER_REV = null;
    USB_ID_STRING: string | null = null;
    DEVICE_TYPE_ID: number | null = null;
    SERIAL_NUM: number | null = null;
    FACTORY_VERSION: number | null = null;
    BATCH_TERMINAL_ID: number | null = null;
    TIMESTAMP: number | null = null;
    FIRMWARE_CRC: number | null = null;
    FIRMWARE_VERSION: number | null = null;
    FIRMWARE_TYPE: number | null = null;
    FIRMWARE_PRODUCT_ID: number | null = null;

    dataView: DataView | null = null;

    checkFirmwareProductId(firmware: any) {
        return this.DEVICE_TYPE_ID === firmware.values.find((f: any) => f.key === this.getMemory().firmware_product_id.toString(16))?.value;
    }

    async readMemory(startAdd: number, endAdd: number) {
        return await this.read(startAdd, endAdd - startAdd + 1).catch((error: any) => {
            console.error("Error reading memory:", error);
            return { isSuccess: false, errorMessage: "Failed to read memory" };
        });
    }

    getMemory() {
        const device_memory: any = {
            blinder: {
                firmware: this.hardware_device_top_address[this.HARDWARE_DEVICE_TYPE!] - 2,
                firmware_product_id: this.hardware_device_top_address[this.HARDWARE_DEVICE_TYPE!],
                factory: 0xfb40,
                name: this.hardware_device_top_address[this.HARDWARE_DEVICE_TYPE!] + 0x7e4 + 1,
                UUID: 0xffc0,
                crc_upper: this.hardware_device_top_address[this.HARDWARE_DEVICE_TYPE!],
                crc_lower: 0
            },
            cobber: {
                firmware: 0x02B0,
                firmware_product_id: 0x02B2,
                factory: 0x7600,
                name: 0x7680,
                crc_upper: 0x6DFF,
                crc_lower: 0x0200
            }
        }

        return device_memory[this.hardware_device_name[this.HARDWARE_DEVICE_TYPE!] !== 'Cobber' ? 'blinder' : 'cobber']
    }

    getDevDetails() {
        const dateTime = new Date((this.TIMESTAMP ?? 0)  * 1000);

        return {
            DETECTED_DEVICE: this.HARDWARE_DEVICE_TYPE ? this.hardware_device_name[this.HARDWARE_DEVICE_TYPE] : 'Unknown',
            DEVICE_UUID: this.DEVICE_UUID ?? 'Unknown',
            BOOTLOADER_REV: this.DEVICE_ID ?? 'Unknown',
            USB_NAME: this.USB_ID_STRING ?? 'Unknown',
            PRODUCT: this.DEVICE_TYPE_ID ? `[0x${this.numberToHex(this.DEVICE_TYPE_ID)}] (${this.product_name[this.DEVICE_TYPE_ID] ?? 'Unkown'})` : 'Unknown',
            BATCH_DATE:`Year [${dateTime.getFullYear()}], Month [${dateTime.getMonth() + 1}], Day [${dateTime.getDate()}]`,
            TERMINAL_ID:`[0x${this.numberToHex(this.BATCH_TERMINAL_ID)}]`,
            SERIAL_NUM:`[0x${this.numberToHex(this.SERIAL_NUM)}]`,
            FIRMWARE_DETAILS: `Version: ${this.FIRMWARE_VERSION} (CRC = 0x${this.FIRMWARE_CRC?.toString(16).padStart(4, '0')})`,
            FIRMWARE_PRODUCT_ID: `[0x${this.numberToHex(this.FIRMWARE_PRODUCT_ID)}] (${this.product_name[this.FIRMWARE_PRODUCT_ID ?? 0] ?? 'Unknown'})`,
            FIRMWARE_TYPE: this.firmware_type_name[this.FIRMWARE_TYPE ?? 0] ?? 'Unkown',
            DEVICE_TYPE: this.DEVICE_TYPE_ID
        }
    }

    findProperty(needle: any, haystack: any, label: string, verbose: boolean = false) {
        if (needle in haystack) {
            // if(verbose) console.log(`${label} => [${needle}] [0x${this.numberToHex(needle)}] (${haystack[needle]})`)
            // else console.log(`${label} => [${haystack[needle] ?? 'Unknown'}]`);
        } else {
            console.log(`${label} => Unknown`, needle);
        }
    }

    logHex(label:string, value: number | null, both: boolean = false) {
        if(!value) console.log(`${label} => Unknown`)
        else console.log(`${label} => [0x${value.toString(16).padStart(2, '0').toUpperCase()}] ${both ? `(${value})` : ''}`);
    }

    numberToHex(value: number | null) {
        return value ? value.toString(16).padStart(2, '0').toUpperCase() : 'Unkown';
    }

    async executePropertyGet(command: () => Promise<any>, callback: any, label: string) {
        try {
            const response = await command();
            if (!response.isSuccess) {
                console.error(`Failed to get ${label}`, response.errorMessage);
            } else {       
                callback(response);
                // console.log(`${label} => ${data ?? 'Retrieved'}`);
            }
        } catch (error) {
            console.error(`Failed to get ${label}`, error);
        }
    }

    async interrogate() {
        await this.selectedDevice.open();

        if (!this.selectedDevice.configuration) {
            console.error("Device has no active configuration.");
            return { isSuccess: false };
        }
        
        const interfaceNumber = 0;
        const foundInterface = this.selectedDevice.configuration.interfaces.find(
            (iface: any) => iface.interfaceNumber === interfaceNumber
        );
        
        if (!foundInterface) {
            console.error(`Interface ${interfaceNumber} not found.`);
            return { isSuccess: false };
        } else if (!foundInterface.claimed) {
            await this.selectedDevice.claimInterface(interfaceNumber)
            .catch((error: any) => {
                console.error(error);
                return null;
            });
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        // console.log("-- interrogateDevice --");
    
        try {
            await this.executePropertyGet(
                () => this.command(this.KNOG_CMD_VERSION, new Uint8Array(0), 5, 60),
                (response: any) => {
                    this.findProperty(this.HARDWARE_DEVICE_TYPE = response.data[2], this.hardware_device_name, "Detected Device")
                    this.DEVICE_ID = response.data[0];
                    this.BOOTLD_ID = response.data[1];
                },
                'Version'
            )

            // Read device UUID from flash memory
            if(this.getMemory().UUID) {
                await this.executePropertyGet(
                    async () => this.read(this.getMemory().UUID, 16),
                    (response: any) => {
                        this.DEVICE_UUID = response.dataList.map((byte: number) => byte.toString(16).padStart(2, '0')).join('');
                        return response.dataList.map((byte: number) => byte.toString(16).padStart(2, '0').toUpperCase()).join(' ');
                    },
                    'Device UUID'
                )
            }
            
            if(this.HARDWARE_DEVICE_TYPE) {
                await this.executePropertyGet(
                    () => this.read(this.getMemory().name, 25),
                    (response: any) => {
                        let usbNameList = response.dataList;
                        let lastIndex = usbNameList.length - 1;
                        while (lastIndex >= 0 && usbNameList[lastIndex] === 255) {
                            lastIndex--;
                        }
                
                        return this.USB_ID_STRING = String.fromCharCode(...usbNameList.slice(0, lastIndex + 1));
                    },
                    'USB Name'
                )
            }

            // console.log("** Factory Data **");
            await this.executePropertyGet(
                () => this.read(this.getMemory().factory, 12),
                (response: any) => {
                    this.dataView = new DataView(new Uint8Array(response.dataList).buffer);
                },
                'Factory Data'
            );

            if(this.dataView) {
                // Get the type
                this.findProperty(this.DEVICE_TYPE_ID = this.dataView.getUint8(0), this.product_name, 'Product ID', true)
                
                // Get the serial
                this.SERIAL_NUM = this.dataView.getUint32(1);
                
                this.FACTORY_VERSION = this.dataView.getUint8(5);
                this.BATCH_TERMINAL_ID = this.dataView.getUint16(6, true);
                this.TIMESTAMP = this.dataView.getUint32(8, true);
            }
    
            // console.log("** Firmware Data **");
            this.FIRMWARE_CRC = await this.getCRC(this.getMemory().crc_lower, this.getMemory().crc_upper);
            await this.executePropertyGet(
                () => this.read(this.getMemory().firmware, 3),
                (firmwareData: any) => {
                    const firmwareDataList: number[] = firmwareData.dataList;
                    this.FIRMWARE_VERSION = firmwareDataList[0]

                    if (firmwareDataList[1] > 3) firmwareDataList[1] = 0;
                    this.FIRMWARE_TYPE = firmwareDataList[1];
            
                    if (firmwareDataList[2] in this.product_name) {
                        this.FIRMWARE_PRODUCT_ID = firmwareDataList[2];
                    }
            
                    if (firmwareDataList[2] !== this.DEVICE_TYPE_ID) {
                        console.log(`!! Mismatch => Firmware & Device Product ID - Firmware(${firmwareDataList[2]}) => Device(${this.DEVICE_TYPE_ID})`);
                    }
                },
                "Firmware"
            )
        } catch (error) {
            console.error(error);
            return { isSuccess: false };
        }
    }

    async updateDevice(blocks: Block[], depth = 0) {
        blocks.forEach((block: Block) => {
            this.scramble(block);
        });

        if(!this.FIRMWARE_PRODUCT_ID && !this.DEVICE_TYPE_ID) return;

        const useCobberMemory = hasCobberSettings(this.FIRMWARE_PRODUCT_ID ?? (this.DEVICE_TYPE_ID ?? 0));

        const memoryBlocks = useCobberMemory ? [
            0x6E00,
            0x7000,
            0x7200
        ] : [
            0xF800,
            0xF840,
            0xF880,
            0xF8C0
        ]

        const keysResponse = await this.setKeys();
        if(!keysResponse.isSuccess) {
            this.log(keysResponse.message);
            return false;
        }
        
        for(const memory of memoryBlocks) {
            if(this.verbose) console.log(`Erasing Block ${memory}`);
            const eraseResponse = await this.erase(memory);
            if(!eraseResponse.isSuccess) {
                this.log(eraseResponse.message);
                return false
            }
        }

        if(this.verbose) console.log("ERASE COMPLETE. WRITING THE BLOCKS...");
        if(blocks) {
            const writeResponse = await this.programBlocks(blocks);
            if(!writeResponse.isSuccess) {
                this.log(writeResponse.message);
                return false;
            }
        }
        
        return true;
    }

    async uploadFirmware(firmware: Firmware, setUploadProgress: Function): Promise<any> {
        const keysResponse = await this.setKeys();
        if(!keysResponse.isSuccess) {
            this.log(keysResponse.message);
            return false;
        }

        const settings = this.firmwareSettings[this.HARDWARE_DEVICE_TYPE ?? 0];
        // Prevent pages being written over
        const payload = firmware.memorypayload.filter(block => block.start_address >= (settings?.after ?? 0));

        const eraseResponse = await this.eraseRange(
            settings.start ?? firmware.eraseblocks.start,
            firmware.eraseblocks.end
        );

        if(!eraseResponse.isSuccess) {
            this.log(eraseResponse.message);
            return false;
        }
    
        const writeResponse = await this.programBlocks(payload, setUploadProgress)
        if(!writeResponse.isSuccess) {
            this.log(writeResponse.message);
            return false;
        }

        return true;
    }

    async getDataRange(start: number, end: number) {
        const blocks = [];
        for(let i = start; i <= end; i += 52) {
            blocks.push({
                start_address: i,
                end_address: Math.min(end, i + 51)
            })
        }

        let data: any = [];
        for(const block of blocks) {
            // console.log('Reading Block', block.start_address, block.end_address);
            data = [...data, ...(await this.getDataSection(block.start_address, block.end_address))];
        }
        return data;
    } 

    async getDataSection(start: number, end: number) {
        const memory: any = await this.readMemory(start, end);
        if (!memory || !memory.isSuccess) {
            console.error(`Failed ${start.toString(16).toUpperCase()} - ${memory.errorMessage}`, memory);
            return [];
        }

        return memory.dataList.map((value: any, index: number) => {
            return {
                address: `0x${(start + index).toString(16).toUpperCase()}`,
                value: parseInt(value.toString())
            };
        });
    }

    memoryToWriteBlock(data: any): Block {
        const block: Block = {
            block: true,
            start_address: parseInt(data[0].address, 16),
            end_address: parseInt(data[data.length - 1].address, 16),
            length: data.length,
            data: data.map((d: any) => d.value)
        }
        this.scramble(block);
        return block;
    }
}

export default USBDevice;